import peachjarLogo from '@/assets/logo.svg';
import { Divider, HeaderFixed, PageTitleHelmet } from '@/components';

import {
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import SpinnerOverlay from '@/components/SpinnerOverlay/SpinnerOverlay';
import { FlyerType } from '@/config/constants';
import { TrackingEvents, TrackingStage } from '@/config/tracking';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { BodyLarge, SearchBox } from '@/design-system';
import { districtVar, subscriptionsVar } from '@/graphql/state';
import { Institution } from '@/graphql/types/Institution';
import {
  useAnalytics,
  useLandingPageTracker,
  useNavigateWithTracking,
} from '@/hooks';
import colors from '@/theme/colors';
import { useReactiveVar } from '@apollo/client';
import 'swiper/css';
import Card from './components/Card/Card';
import { ChildrensWakingHours } from './components/ChildrensWakingHours/ChildrensWakingHours';
import { CustomizeYourCommunity } from './components/CustomizeYourCommunity/CustomizeYourCommunity';
import { TreesSavedCounter } from './components/TreesSavedCounter';
import styles from './Home.styles';

export const Home: React.FC = () => {
  const subscriptions = useReactiveVar(subscriptionsVar);
  const { community } = useContext(UserPreferencesContext);
  const district = useReactiveVar(districtVar);
  const { trackEvent } = useAnalytics();
  const [scrollTop, setScrollTop] = useState(0);
  const [searchInputValue, setSearchQuery] = useState('');
  const navigateWithTracking = useNavigateWithTracking();

  const trackingEventFrom = 'home';

  const schoolList = useMemo(() => {
    const schools: Institution[] = [];
    subscriptions.forEach(sub => {
      schools.push({
        type: FlyerType.school,
        institutionId: sub.id,
        institutionName: sub.name,
        informations: sub.flyerCounters || [],
      });
    });

    return schools;
  }, [subscriptions]);

  useLandingPageTracker(() => {
    trackEvent({
      event: TrackingEvents.browseContent,
      event_detail: trackingEventFrom,
      stage: TrackingStage.discovery,
    });
  });

  const communityList = useMemo(
    () => [
      {
        type: FlyerType.community,
        institutionId: 'all',
        institutionName: community.localArea,
        informations: community.flyerCounters || [],
      },
    ],
    [community],
  );

  const handler = useCallback(() => {
    setScrollTop(document.body.scrollTop || document.documentElement.scrollTop);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const onSearchInputKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Enter' && searchInputValue.length > 0) {
      const tracking = {
        event_from: TrackingEvents.browseContent,
        event_detail_from: trackingEventFrom,
        stage_from: TrackingStage.discovery,
      };
      const encodedSearch = encodeURIComponent(searchInputValue);
      navigateWithTracking(`/explore/${encodedSearch}/results`, tracking);
    }
  };

  if (!schoolList || !communityList) return <SpinnerOverlay loading />;

  return (
    <>
      <PageTitleHelmet>Home</PageTitleHelmet>
      <HeaderFixed>
        <div className={scrollTop >= 800 ? styles.headerShrunk : styles.header}>
          <img
            src={peachjarLogo}
            alt="Peachjar Logo"
            width="101px"
            height="32px"
            data-testid="peachjarLogo"
            loading="lazy"
          />
        </div>
        {scrollTop === 0 && (
          <div className={styles.titleContainer}>
            <BodyLarge color={colors.slate}>
              Thriving Beyond the Classroom
            </BodyLarge>
          </div>
        )}
        <Divider />
        {scrollTop === 0 && (
          <div className={styles.searchContainer}>
            <SearchBox
              placeholder="Search..."
              onChange={setSearchQuery}
              isTextBold
              value={searchInputValue}
              onKeyDown={onSearchInputKeyDown}
              showCancelButton
            />
          </div>
        )}
      </HeaderFixed>
      <main className={styles.container} data-testid="homeContainer">
        <div className={styles.flyerCards}>
          {schoolList && (
            <Card
              districtId={district?.districtId}
              variant={FlyerType.school}
              list={schoolList}
            />
          )}
          {communityList && (
            <Card
              districtId={district?.districtId}
              variant={FlyerType.community}
              list={communityList}
            />
          )}
        </div>
        <ChildrensWakingHours subscriptions={subscriptions} />
        {district?.name && (
          <TreesSavedCounter
            district={district.name}
            treeCounter={district.treeCounter ?? 0}
            sheetCounter={district.sheetCounter ?? 0}
          />
        )}
        {!community.isSaved && <CustomizeYourCommunity />}
      </main>
    </>
  );
};

export default Home;
